import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Play, Pause, Broadcast, Warning, Gauge, ShieldWarning,
  SpinnerGap, Crosshair,
} from "@phosphor-icons/react";

const SEV = {
  critical: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40",
  high: "bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/40",
  medium: "bg-[#0A84FF]/15 text-[#0A84FF] border-[#0A84FF]/40",
  low: "bg-[#71717A]/15 text-[#A1A1AA] border-[#71717A]/40",
};

function Slider({ label, value, onChange, icon: Icon, min = 0, max = 100 }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#A1A1AA]">
          <Icon size={14} weight="bold" /> {label}
        </span>
        <span className="font-mono-data text-sm text-white">{value}</span>
      </div>
      <input
        type="range" min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#007AFF] cursor-pointer"
        data-testid={`slider-${label.toLowerCase().replace(/\s+/g, "-")}`}
      />
    </div>
  );
}

export default function LiveWatch() {
  const [cameras, setCameras] = useState([]);
  const [watching, setWatching] = useState(false);
  const [intervalSec, setIntervalSec] = useState(15);
  const [theft, setTheft] = useState(70);
  const [vandalism, setVandalism] = useState(70);
  const [effMin, setEffMin] = useState(45);
  const [scanning, setScanning] = useState(null);
  const [log, setLog] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const idxRef = useRef(0);
  const busyRef = useRef(false);
  const watchingRef = useRef(false);
  const scanRef = useRef(null);
  const cfgRef = useRef({ theft, vandalism, effMin });
  cfgRef.current = { theft, vandalism, effMin };

  useEffect(() => {
    api.get("/cameras").then((r) => setCameras(r.data));
  }, []);

  const onlineCams = useMemo(() => cameras.filter((c) => c.status === "online"), [cameras]);

  const scanOne = useCallback(async () => {
    if (busyRef.current || onlineCams.length === 0) return;
    busyRef.current = true;
    const cam = onlineCams[idxRef.current % onlineCams.length];
    idxRef.current += 1;
    setScanning(cam.id);
    try {
      const { data } = await api.post("/monitor/scan", {
        camera_id: cam.id,
        theft_threshold: cfgRef.current.theft,
        vandalism_threshold: cfgRef.current.vandalism,
        effectiveness_min: cfgRef.current.effMin,
      });
      if (!watchingRef.current) return; // discard results that land after Stop
      const entry = {
        id: `${cam.id}-${Date.now()}`,
        camera: data.camera,
        ts: data.scanned_at || new Date().toISOString(),
        theft: data.result?.theft_risk?.score ?? 0,
        vandalism: data.result?.vandalism_risk?.score ?? 0,
        eff: data.result?.employee_effectiveness?.score ?? 0,
        summary: data.result?.scene_summary || "",
        alerts: data.alerts || [],
      };
      setLog((prev) => [entry, ...prev].slice(0, 40));
      if (entry.alerts.length) {
        setAlerts((prev) => [...entry.alerts, ...prev].slice(0, 30));
        entry.alerts.forEach((a) => {
          const fn = a.severity === "critical" || a.severity === "high" ? toast.error : toast.warning;
          fn(`${a.type} · ${a.camera}`, { description: a.message });
          if ((a.severity === "critical" || a.severity === "high") && "Notification" in window && Notification.permission === "granted") {
            new Notification(`SentryVision · ${a.type} (${a.severity})`, { body: `${a.camera}: ${a.message}` });
          }
        });
      }
    } catch (e) {
      if (watchingRef.current) {
        toast.error(`Scan failed · ${cam.name}`, { description: e.response?.data?.detail || "Could not analyze feed" });
        setLog((prev) => [{ id: `${cam.id}-err-${Date.now()}`, camera: cam.name, ts: new Date().toISOString(), theft: 0, vandalism: 0, eff: 0, summary: "", alerts: [], error: true }, ...prev].slice(0, 40));
      }
    } finally {
      setScanning(null);
      busyRef.current = false;
    }
  }, [onlineCams]);

  scanRef.current = scanOne;

  useEffect(() => {
    watchingRef.current = watching;
    if (!watching) return;
    scanRef.current && scanRef.current();
    const t = setInterval(() => { scanRef.current && scanRef.current(); }, intervalSec * 1000);
    return () => clearInterval(t);
  }, [watching, intervalSec]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      {/* Control panel */}
      <div className="xl:col-span-4 space-y-4">
        <div className="bg-[#121214] border border-white/10 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
              <Broadcast size={16} weight="bold" className="text-[#007AFF]" /> Watch Engine
            </h2>
            <span className={`font-mono-data text-[10px] px-2 py-0.5 border uppercase tracking-wider ${watching ? "bg-[#32D74B]/15 text-[#32D74B] border-[#32D74B]/40" : "bg-[#71717A]/15 text-[#A1A1AA] border-[#71717A]/40"}`}>
              {watching ? "● Live" : "Idle"}
            </span>
          </div>

          <button
            data-testid="watch-toggle-button"
            onClick={() => setWatching((w) => !w)}
            disabled={onlineCams.length === 0}
            className={`w-full py-3 font-display font-bold uppercase tracking-wide text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${watching ? "bg-[#FF3B30] hover:bg-[#ff5c52] text-white" : "bg-[#007AFF] hover:bg-[#3395FF] text-white"}`}
          >
            {watching ? <><Pause size={18} weight="fill" /> Stop Monitoring</> : <><Play size={18} weight="fill" /> Start Monitoring</>}
          </button>
          <p className="text-xs text-[#71717A] mt-3 leading-relaxed">
            Auto-analyzes each online feed with the AI vision model on a rolling loop and raises an alert the instant a threshold is crossed. {onlineCams.length} online feed(s) in rotation.
          </p>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Scan Interval</span>
              <span className="font-mono-data text-sm text-white">{intervalSec}s</span>
            </div>
            <input type="range" min={8} max={60} value={intervalSec} onChange={(e) => setIntervalSec(Number(e.target.value))} className="w-full accent-[#007AFF] cursor-pointer" data-testid="slider-interval" />
          </div>
        </div>

        <div className="bg-[#121214] border border-white/10 p-4 space-y-5">
          <h3 className="font-display font-black uppercase tracking-tighter text-xs text-[#A1A1AA]">Alert Thresholds</h3>
          <Slider label="Theft Risk" value={theft} onChange={setTheft} icon={Warning} />
          <Slider label="Vandalism Risk" value={vandalism} onChange={setVandalism} icon={ShieldWarning} />
          <Slider label="Min Effectiveness" value={effMin} onChange={setEffMin} icon={Gauge} />
          <p className="text-[11px] text-[#A1A1AA] font-mono-data leading-relaxed">
            Alerts fire when theft/vandalism rises above its threshold, or staff effectiveness falls below the minimum. AI output is decision-support — always review the clip.
          </p>
        </div>
      </div>

      {/* Live feed status + log */}
      <div className="xl:col-span-8 space-y-4">
        <div>
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-3">Feed Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {cameras.map((c) => {
              const active = scanning === c.id;
              return (
                <div key={c.id} data-testid={`watch-cam-${c.id}`} className={`relative aspect-video bg-black border overflow-hidden ${active ? "border-[#007AFF]" : "border-white/10"}`}>
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover opacity-60" />
                  <div className="absolute inset-0 bg-black/40" />
                  {active && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Crosshair size={28} className="text-[#007AFF] animate-spin" style={{ animationDuration: "2s" }} />
                    </div>
                  )}
                  <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
                    {c.status === "online"
                      ? <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] live-dot" />
                      : <span className="font-mono-data text-[9px] text-[#71717A]">OFFLINE</span>}
                    {active && <span className="font-mono-data text-[9px] text-[#007AFF]">SCANNING</span>}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/90 to-transparent">
                    <p className="text-[11px] font-medium leading-none truncate">{c.name}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Alerts */}
          <div className="bg-[#121214] border border-white/10">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display font-black uppercase tracking-tighter text-xs">Live Alerts</h3>
              <span data-testid="watch-alert-count" className="font-mono-data text-[10px] px-2 py-0.5 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40">{alerts.length}</span>
            </div>
            <div className="p-3 space-y-2 max-h-[340px] overflow-y-auto">
              {alerts.length === 0 && <p className="text-xs text-[#71717A] font-mono-data p-2">No alerts raised yet. Start monitoring.</p>}
              <AnimatePresence>
                {alerts.map((a) => (
                  <motion.div key={a.id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} className="border border-white/10 p-2.5" data-testid={`watch-alert-${a.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-mono-data text-[10px] px-1.5 py-0.5 border uppercase ${SEV[a.severity]}`}>{a.type}</span>
                      <span className="font-mono-data text-[10px] text-[#71717A]">{new Date(a.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                    </div>
                    <p className="text-xs text-[#A1A1AA] leading-snug">{a.message}</p>
                    <p className="font-mono-data text-[10px] text-[#71717A] mt-1">{a.camera}</p>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Scan log */}
          <div className="bg-[#121214] border border-white/10">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display font-black uppercase tracking-tighter text-xs">Scan Log</h3>
              {scanning && <SpinnerGap size={14} className="animate-spin text-[#007AFF]" />}
            </div>
            <div className="p-3 space-y-1.5 max-h-[340px] overflow-y-auto" data-testid="watch-scan-log">
              {log.length === 0 && <p className="text-xs text-[#71717A] font-mono-data p-2">Awaiting first scan…</p>}
              {log.map((e) => (
                <div key={e.id} data-testid={`scan-row-${e.id}`} className="border-b border-white/5 pb-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">{e.camera}</span>
                    <span className="font-mono-data text-[10px] text-[#71717A]">{new Date(e.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                  </div>
                  <div className="flex gap-3 font-mono-data text-[10px] mt-1">
                    <span style={{ color: e.theft >= theft ? "#FF3B30" : "#71717A" }}>THEFT {e.theft}</span>
                    <span style={{ color: e.vandalism >= vandalism ? "#FF3B30" : "#71717A" }}>VAND {e.vandalism}</span>
                    <span style={{ color: e.eff <= effMin ? "#FF9F0A" : "#71717A" }}>EFF {e.eff}</span>
                    {e.alerts.length > 0 && <span className="text-[#FF3B30]">⚠ {e.alerts.length} ALERT</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
